<script lang="ts">
    import AppLogo from '@/components/AppLogo.svelte';
    import AppLogoIcon from '@/components/AppLogoIcon.svelte';
    import Breadcrumbs from '@/components/Breadcrumbs.svelte';
    import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
    import { Button } from '@/components/ui/button';
    import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
    import { Menubar, MenubarMenu, MenubarTrigger } from '@/components/ui/menubar';
    import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
    import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
    import UserMenuContent from '@/components/UserMenuContent.svelte';
    import { getInitials } from '@/hooks/useInitials';
    import type { BreadcrumbItem } from '@/types';
    import { Link, page } from '@inertiajs/svelte';
    import { cva } from 'class-variance-authority';
    import {
        Menu, Search, PersonStanding, Hammer, Truck, IdCardLanyard, Calendar, FileText,
        Lightbulb, ChartNoAxesCombined, ClipboardList
    } from 'lucide-svelte';
    import type {ActionButtonType, RelatedListButtonType, NavItemType, SectionLinkType} from "@/types/types";
    import ConfirmPopoverButton from "@/components/ConfirmPopoverButton.svelte";
    import { inertia } from '@inertiajs/svelte';
    import ChevronRightIcon from '@lucide/svelte/icons/chevron-right';
    import ChevronLeftIcon from '@lucide/svelte/icons/chevron-left';
    import { onMount } from 'svelte';

    interface Props {
        breadcrumbs?: BreadcrumbItem[];
        actionbuttons?: ActionButtonType[];
        relatedLists?: RelatedListButtonType[];
        sectionLinks?: SectionLinkType[];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let { breadcrumbs = [], actionbuttons = [], relatedLists = [], sectionLinks = []}: Props = $props();

    function scrollToSection(id: string) {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    let user = $derived($page.props.auth.user);
    let activityCount = $derived(($page.props.activityCount as number) ?? 0);

    let headerRef: HTMLDivElement | null = $state(null);

    // Poll activity count using fetch (avoids interfering with form state during Inertia reloads)
    let polledActivityCount = $state<number | null>(null);
    let displayActivityCount = $derived(polledActivityCount ?? activityCount);

    onMount(() => {
        const refreshActivityCount = async () => {
            try {
                const response = await fetch('/web-api/activity-count');
                if (response.ok) {
                    const data = await response.json();
                    polledActivityCount = data.count ?? 0;
                }
            } catch {
                // Silently ignore polling errors
            }
        };

        // Small delay to let page settle after login
        const initialTimeout = setTimeout(refreshActivityCount, 500);
        const interval = setInterval(refreshActivityCount, 10000);

        return () => {
            clearTimeout(initialTimeout);
            clearInterval(interval);
        };
    });


    // Expose header height as a CSS custom property for sticky sub-headers
    let headerHeight = $state(0);
    $effect(() => {
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    });

    // Track if header is stuck using scroll position
    let isStuck = $state(false);

    $effect(() => {
        const handleScroll = () => {
            isStuck = window.scrollY > 0;
        };

        // Check initial state
        handleScroll();

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    });

    const isCurrentRoute = $derived((url: string) => $page.url === url);

    const activeItemStyles = $derived((url: string) => (isCurrentRoute(url) ? 'text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100' : ''));

    const navigationMenuTriggerStyle = cva(
        `group bg-background hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-active:bg-accent/50 data-[state=open]:bg-accent/50 inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-hidden disabled:pointer-events-none disabled:opacity-50`,
    );

    const mainNavItems: NavItemType[] = [
        {
            title: 'Dashboard',
            href: '/dashboard',
            icon: ChartNoAxesCombined,
        },
        {
            title: 'Activity',
            href: '/activity',
            icon: ClipboardList,
        },
        {
            title: 'Leads',
            href: '/leads',
            icon: Lightbulb,
        },
        {
            title: 'Customers',
            href: '/customers',
            icon: PersonStanding,
        },
        // {
        //     title: 'Sites',
        //     href: '/sites',
        //     icon: MapPinned,
        // },
        {
            title: 'Jobs',
            href: '/jobs',
            icon: Hammer,
        },
        {
            title: 'Schedular',
            href: '/schedular',
            icon: Calendar,
        },
        {
            title: 'Vehicles',
            href: '/vehicles',
            icon: Truck,
        },
        {
            title: 'Employees',
            href: '/employees',
            icon: IdCardLanyard,
        },
        {
            title: 'Documents',
            href: '/documents',
            icon: FileText,
        },
    ];

    const rightNavItems: NavItemType[] = [
        // {
        //     title: 'Repository',
        //     href: 'https://github.com/oseughu/svelte-starter-kit',
        //     icon: Folder,
        // },
        // {
        //     title: 'Documentation',
        //     href: 'https://laravel.com/docs/starter-kits',
        //     icon: BookOpen,
        // },
    ];
</script>

<!-- Sentinel element to detect when header becomes stuck -->
<div bind:this={headerRef} class="absolute top-0 h-px w-full"></div>

<div class="sticky top-0 z-50 bg-background" bind:clientHeight={headerHeight}>
    <div class="border-b border-sidebar-border/80">
        <div class="flex items-center px-4 mx-2 md:mx-8 lg:mx-16 xl:mx-32 transition-all duration-200 {isStuck ? 'h-10' : 'h-16'}">
            <div class="2xl:hidden">
                <Sheet>
                    <SheetTrigger>
                        <Button variant="ghost" size="icon" class="mr-2 h-9 w-9">
                            <Menu class="h-5 w-5" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="left" class="w-[300px] p-6">
                        <SheetTitle class="sr-only">Navigation Menu</SheetTitle>
                        <SheetHeader class="flex justify-start text-left">
                            <AppLogoIcon class="size-6 fill-current text-black dark:text-white" />
                        </SheetHeader>
                        <div class="flex h-full flex-1 flex-col justify-between space-y-4 py-6">
                            <nav class="-mx-3 space-y-1">
                                {#each mainNavItems as item (item.title)}
                                    <!-- eslint-disable-next-line svelte/no-navigation-without-resolve -->
                                    <a href={item.href} use:inertia={{ prefetch: true, cacheFor: 3000 }} class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent {activeItemStyles(item.href)}"
                                    >
                                        {#if item.icon}
                                            <item.icon class="h-5 w-5" />
                                        {/if}
                                        {item.title}
                                        {#if item.href === '/activity' && displayActivityCount > 0}
                                            <span class="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold text-white" style="background-color: var(--error);">
                                                {displayActivityCount > 99 ? '99+' : displayActivityCount}
                                            </span>
                                        {/if}
                                    </a>
                                {/each}
                            </nav>
                            <div class="flex flex-col space-y-4">
                                {#each rightNavItems as item (item.title)}
                                    <Link href={item.href} class="flex items-center space-x-2 text-sm font-medium">
                                        {#if item.icon}
                                            <item.icon class="h-5 w-5" />
                                        {/if}
                                        <span>{item.title}</span>
                                    </Link>
                                {/each}
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
            </div>

            <Link href={route('dashboard')} class="flex items-center gap-x-2 transition-all duration-200 overflow-hidden {isStuck ? 'w-0 opacity-0' : 'w-auto opacity-100'}">
                <AppLogo />
            </Link>

            <!-- Desktop Menu -->
            <div class="hidden 2xl:flex h-full flex-1 h-full">
                <Menubar class="flex h-full items-center border-none bg-transparent">
                    {#each mainNavItems as item, index (index)}
                        <MenubarMenu>
                            <!-- eslint-disable-next-line svelte/no-navigation-without-resolve -->
                            <a href={item.href} use:inertia={{ prefetch: true, cacheFor: 3000 }} class="space-x-2 h-full">
                            <MenubarTrigger
                                value={item.href}
                                class="{navigationMenuTriggerStyle()} {activeItemStyles(
                                    item.href,
                                )} relative flex h-full cursor-pointer items-center px-4 text-sm font-medium"
                            >

                                    {#if item.icon}
                                        <item.icon class="mr-2 h-4 w-4" />
                                    {/if}
                                    {item.title}
                                    {#if item.href === '/activity' && displayActivityCount > 0}
                                        <span class="ml-1.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold text-white" style="background-color: var(--error);">
                                            {displayActivityCount > 99 ? '99+' : displayActivityCount}
                                        </span>
                                    {/if}
                                    {#if isCurrentRoute(item.href)}
                                        <div class="absolute bottom-0 left-0 h-0.5 w-full translate-y-px bg-black dark:bg-white"></div>
                                    {/if}

                            </MenubarTrigger>
                            </a>
                        </MenubarMenu>
                    {/each}
                </Menubar>
            </div>

            <div class="ml-auto flex items-center space-x-2">
                <div class="relative flex items-center space-x-1">
                    <Button variant="ghost" size="icon" class="group h-9 w-9 cursor-pointer">
                        <Search class="size-5 opacity-80 group-hover:opacity-100" />
                    </Button>

                    <div class="hidden space-x-1 lg:flex">
                        {#each rightNavItems as item (item.title)}
                            <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <!-- eslint-disable-next-line svelte/no-navigation-without-resolve -->
                                        <a href={item.href} target="_blank" rel="noopener noreferrer">
                                            <Button variant="ghost" size="icon" class="group h-9 w-9 cursor-pointer">
                                                    <span class="sr-only">{item.title}</span>
                                                    <item.icon class="size-5 opacity-80 group-hover:opacity-100" />

                                            </Button>
                                        </a>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{item.title}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        {/each}
                    </div>
                </div>

                <DropdownMenu>
                    <DropdownMenuTrigger>
                        <Button
                            variant="ghost"
                            size="icon"
                            class="relative size-10 w-auto rounded-full p-1 focus-within:ring-2 focus-within:ring-primary"
                        >
                            <Avatar class="size-8 overflow-hidden rounded-full">
                                {#if user.avatar}
                                    <AvatarImage src={user.avatar} alt={user.name} />
                                {:else}
                                    <AvatarFallback class="rounded-lg bg-neutral-200 font-semibold text-black dark:bg-neutral-700 dark:text-white">
                                        {getInitials(user.name || '')}
                                    </AvatarFallback>
                                {/if}
                            </Avatar>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" class="w-56">
                        <UserMenuContent {user} />
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    </div>
<!--    <Debug {breadcrumbs} />-->

    {#if breadcrumbs.length > 1}
        <div class="border-b border-sidebar-border/70 px-4 mx-2 md:mx-8 lg:mx-16 xl:mx-32">
            <div class="flex items-center text-neutral-500 transition-all duration-200 {isStuck ? 'py-1' : 'py-3'}">
                <div class="flex-shrink-0">
                    <Breadcrumbs {breadcrumbs} />
                </div>
                {#if sectionLinks.length > 0}
                    <ChevronRightIcon class="h-4 flex-shrink-0 mx-1"/>
                    <div class="relative flex-1 min-w-0 flex items-center">
                        <!-- Left scroll button -->
                        <button
                            type="button"
                            class="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-6 w-6 flex items-center justify-center rounded-full bg-background/90 shadow-sm border border-border hover:bg-accent transition-opacity duration-200 opacity-0"
                            id="section-scroll-left-btn"
                            onclick={() => {
                                const container = document.getElementById('section-links-container');
                                if (container) container.scrollBy({ left: -150, behavior: 'smooth' });
                            }}
                        >
                            <ChevronLeftIcon class="h-4 w-4" />
                        </button>
                        <!-- Right scroll button -->
                        <button
                            type="button"
                            class="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-6 w-6 flex items-center justify-center rounded-full bg-background/90 shadow-sm border border-border hover:bg-accent transition-opacity duration-200"
                            id="section-scroll-right-btn"
                            onclick={() => {
                                const container = document.getElementById('section-links-container');
                                if (container) container.scrollBy({ left: 150, behavior: 'smooth' });
                            }}
                        >
                            <ChevronRightIcon class="h-4 w-4" />
                        </button>
                        <div
                            id="section-links-container"
                            class="flex items-center gap-1 overflow-x-auto scrollbar-hide scroll-smooth mx-7"
                            onscroll={(e) => {
                                const target = e.currentTarget;
                                const leftBtn = document.getElementById('section-scroll-left-btn');
                                const rightBtn = document.getElementById('section-scroll-right-btn');
                                if (leftBtn) leftBtn.style.opacity = target.scrollLeft > 0 ? '1' : '0';
                                if (rightBtn) rightBtn.style.opacity = target.scrollLeft < (target.scrollWidth - target.clientWidth - 5) ? '1' : '0';
                            }}
                        >
                            {#each sectionLinks as link (link.id)}
                                <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onclick={() => scrollToSection(link.id)}
                                                class="flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 flex-shrink-0 whitespace-nowrap"
                                            >
                                                {#if link.icon}
                                                    <svelte:component this={link.icon} class="h-3.5 w-3.5" />
                                                {/if}
                                                <span class="hidden md:inline">{link.label}</span>
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent class="md:hidden">
                                            <p>{link.label}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            {/each}
                        </div>
                    </div>
                {/if}
            </div>
        </div>
    {/if}
    {#if actionbuttons.length >= 1}
        <div class="flex w-full border-b border-sidebar-border/70">
            <div class="flex w-full items-center justify-start text-neutral-500 px-4 mx-2 md:mx-8 lg:mx-16 xl:mx-32 gap-4 transition-all duration-200 {isStuck ? 'py-1 gap-2' : 'py-3'}">
                {#each actionbuttons as item (item.title)}
                    {#if item.actionType === 'delete'}
                        <ConfirmPopoverButton
                            title={item.title}
                            icon={item.icon}
                            message="Are you sure you want to delete this customer?"
                            onConfirm={item.onClick}
                            size={isStuck ? 'sm' : 'default'}
                        />
                    {:else if item.href}
                    <Button
                        href={item.href}
                        size={isStuck ? 'sm' : 'default'}
                        class="flex items-center rounded-lg font-medium hover:bg-accent transition-all duration-200 {isStuck ? 'gap-x-1.5 px-2 py-1 text-xs' : 'gap-x-3 px-3 py-2 text-sm'}" >
                        {#if item.icon}
                            <item.icon class="transition-all duration-200 {isStuck ? 'h-3.5 w-3.5' : 'h-5 w-5'}" />  {item.title}
                        {/if}
                    </Button>
                    {:else}
                    <Button
                        onclick={item.onClick}
                        size={isStuck ? 'sm' : 'default'}
                        class="flex items-center rounded-lg font-medium hover:bg-accent transition-all duration-200 {isStuck ? 'gap-x-1.5 px-2 py-1 text-xs' : 'gap-x-3 px-3 py-2 text-sm'}">
                        {#if item.icon}
                            <item.icon class="transition-all duration-200 {isStuck ? 'h-3.5 w-3.5' : 'h-5 w-5'}" />  {item.title}
                        {/if}
                    </Button>
                    {/if}

                {/each}
            </div>
        </div>
    {/if}
</div>
